export default {
  state: {
    // Allowance v1->v2 (Slice 4): the ``companyAllowance`` state (fed by the
    // dropped ``stores.company_allowance`` column) had no readers and was
    // removed. Shopper allowance is a v2 allowance-policy concept.
    collectSalesTaxEnabled: false,
    collectProcessingFeeEnabled: false,
    consolidateTaxFeeEnabled: false,
    editStoreName: "",
    companyEmailEnabled: false,
    storefrontPricing: false,
    disableNextButton: false,
    productsLoaded: false,
    distributorLoadPending: true,
    newOrderAdded: false,
    decorationPlacements: [],
    decorationsMethods: [],
    decorationNames: [],
    brandColor: null,
    // Issue #2178: the storefront brand banner (StorefrontLayout topbar)
    // shows the store name, but the layout is a slot wrapper that does not
    // hold the shopper view's signInInfo. StorefrontViewShopper commits the
    // name here (mirroring setBrandColor); the layout reads getStoreName.
    // null until a storefront loads; the layout falls back to "Shop".
    storeName: null,
    // The URL slug the stored storeName / brand state belongs to. Lets the
    // browser-tab hook refuse a name that was committed for ANOTHER store
    // (storefront A -> storefront B before B's payload lands).
    storeSlug: null,
    // Store logo overlay (Trello uGbbyBlq): resolved brand_image URL for
    // the storefront brand banner. Mirrors brandColor / storeName -- set
    // by useStorefrontChrome, read by StorefrontLayout. null means "no
    // logo uploaded" (the common case) and the layout renders no <img>.
    brandLogoUrl: null,
    // The browser-tab FAVICON for the storefront on screen (Unit 12):
    // the resolved `white_label_favicon` URL, or null. Set by
    // useStorefrontChrome from the SAME payload as brandLogoUrl.
    //
    // SEPARATE FROM brandLogoUrl because a favicon is a different asset
    // from a logo -- square and legible at 16x16, where a brand image is
    // often a wordmark that turns to mush at tab size. null means "no
    // favicon uploaded", and the tab hook falls through to the brand image
    // and then to the ThreadFlow default.
    brandFaviconUrl: null,
    // White-label answer for the storefront currently on screen, as returned
    // by `getStorefrontCampaign` -- `{enabled, logo_key, attribution}`, the
    // exact shape `services/white_label.white_label_for_store` returns. Set by
    // useStorefrontChrome alongside brandColor / storeName / brandLogoUrl,
    // because it rides the SAME payload and a second fetch would mean the
    // surfaces disagree for one paint.
    //
    // null means "no store loaded yet", which every reader must treat as NOT
    // white-labeled rather than as unknown: an absent answer and a disabled one
    // must render identically, or an in-flight request would flash a brand.
    storefrontWhiteLabel: null,
    smartShippingEnabled: false,
    productsIncomplete: false,
    isBFLCostChanged: false,
    isClickedExitButton: false,
    tabsIncomplete: null,
    productEditModalPopped: false,
    fieldCustomEnabled: false,
    bflCostChanged: false,
    shopperAccountChanged: false,
    locationsChanged: false,
    shopperURLChanged: false,
    // Claude Design redesign experiment — autosave indicator state.
    // 'saved' (green), 'saving' (yellow), 'dirty' (red, unsaved changes
    // pending the debounce), 'error' (red, save failed). Read by
    // TheTopBar. Set by CampaignAddEdit's autosave debounce.
    autoSaveState: 'saved',
  },

  mutations: {
    setCollectSalesTaxEnabled(state, payload) {
      state.collectSalesTaxEnabled = payload;
    },
    setSmartShippingEnabled(state, payload) {
      state.smartShippingEnabled = payload;
    },
    setCollectProcessingFeeEnabled(state, payload) {
      state.collectProcessingFeeEnabled = payload;
    },
    setConsolidateTaxFeeEnabled(state, payload) {
      state.consolidateTaxFeeEnabled = payload;
    },
    setEditStoreName(state, payload) {
      state.editStoreName = payload;
    },
    setCompanyEmailEnabled(state, payload) {
      state.companyEmailEnabled = payload;
    },
    setStorefrontPricing(state, payload) {
      state.storefrontPricing = payload;
    },
    setDisableNextButton(state, payload) {
      state.disableNextButton = payload;
    },
    setProductsLoaded(state, payload) {
      state.productsLoaded = payload;
    },
    setDistributorLoadPending(state, payload) {
      state.distributorLoadPending = payload;
    },
    setNewOrderAdded(state, payload) {
      state.newOrderAdded = payload;
    },
    setDecorationPlacements(state, payload) {
      state.decorationPlacements = payload;
    },
    setDecorationsMethods(state, payload) {
      state.decorationsMethods = payload;
    },
    setDecorationNames(state, payload) {
      state.decorationNames = payload;
    },
    setBrandColor(state, payload) {
      state.brandColor = payload;
    },
    setStoreName(state, payload) {
      state.storeName = payload;
    },
    setStoreSlug(state, payload) {
      state.storeSlug = payload;
    },
    setBrandLogoUrl(state, payload) {
      state.brandLogoUrl = payload;
    },
    setBrandFaviconUrl(state, payload) {
      state.brandFaviconUrl = payload;
    },
    setStorefrontWhiteLabel(state, payload) {
      state.storefrontWhiteLabel = payload;
    },
    setProductsIncomplete(state, payload) {
      state.productsIncomplete = payload;
    },
    editIncompleteProductList(state, payload) {
      state.incompleteProductList[payload.id] = payload.value;
    },
    setBFLCostChanged(state, payload) {
      state.isBFLCostChanged = payload;
    },
    setIsClickedExitButton(state, payload) {
      state.isClickedExitButton = payload;
    },
    setTabsIncomplete(state, payload) {
      state.tabsIncomplete = payload;
    },
    setAutoSaveState(state, payload) {
      // Allowed values: 'saved' | 'saving' | 'dirty' | 'error'
      state.autoSaveState = payload;
    },
    setProductEditModalPopped(state, payload) {
      state.productEditModalPopped = payload;
    },
    setFieldCustomEnabled(state, payload) {
      state.fieldCustomEnabled = payload;
    },
    setShopperAccountChanged(state, payload) {
      state.shopperAccountChanged = payload;
    },
    setShopperURLChanged(state, payload) {
      state.shopperURLChanged = payload;
    },
    setLocationsChanged(state, payload) {
      state.locationsChanged = payload;
    },
  },

  actions: {
    setCampaignCollectSalesTaxEnabled({ commit }, payload) {
      commit("setCollectSalesTaxEnabled", payload);
    },
    setCampaignSmartShippingenabled({ commit }, payload) {
      commit("setSmartShippingEnabled", payload);
    },
    setCampaignCollectProcessingFeeEnabled({ commit }, payload) {
      commit("setCollectProcessingFeeEnabled", payload);
    },
    setCampaignConsolidateTaxFeeEnabled({ commit }, payload) {
      commit("setConsolidateTaxFeeEnabled", payload);
    },
    setEditedCampaignName({ commit }, payload) {
      commit("setEditStoreName", payload);
    },
    setCampaignCompanyEmailEnabled({ commit }, payload) {
      commit("setCompanyEmailEnabled", payload);
    },
    setCampaignStorefrontPricing({ commit }, payload) {
      commit("setStorefrontPricing", payload);
    },
    setCampaignDisableNextButton({ commit }, payload) {
      commit("setDisableNextButton", payload);
    },
    setProductsLoaded({ commit }, payload) {
      commit("setProductsLoaded", payload);
    },
    setCampaignDistributorLoadPending({ commit }, payload) {
      commit("setDistributorLoadPending", payload);
    },
    setCampaignNewOrderAdded({ commit }, payload) {
      commit("setNewOrderAdded", payload);
    },
    setCampaignDecorationPlacements({ commit }, payload) {
      commit("setDecorationPlacements", payload);
    },
    setCampaignDecorationsMethods({ commit }, payload) {
      commit("setDecorationsMethods", payload);
    },
    setCampaignDecorationNames({ commit }, payload) {
      commit("setDecorationNames", payload);
    },
    setCampaignBrandColor({ commit }, payload) {
      commit("setBrandColor", payload);
    },
    setCampaignBFLCostChanged({ commit }, payload) {
      commit("setBFLCostChanged", payload);
    },
    setCampaignIsClickedExitButton({ commit }, payload) {
      commit("setIsClickedExitButton", payload);
    },
    setCampaignFieldCustomEnabled({ commit }, payload) {
      commit("setFieldCustomEnabled", payload);
    },
    setShopperAccountChanged({ commit }, payload) {
      commit("setShopperAccountChanged", payload);
    },
    setShopperURLChanged({ commit }, payload) {
      commit("setShopperURLChanged", payload);
    },
    setLocationsChanged({ commit }, payload) {
      commit("setLocationsChanged", payload);
    },
  },

  getters: {
    getCollectSalesTaxEnabled(state) {
      return state.collectSalesTaxEnabled;
    },
    getSmartShippingEnabled(state) {
      return state.smartShippingEnabled;
    },
    getCollectProcessingFeeEnabled(state) {
      return state.collectProcessingFeeEnabled;
    },
    getConsolidateTaxFeeEnabled(state) {
      return state.consolidateTaxFeeEnabled;
    },
    getEditedCampaignName(state) {
      return state.editStoreName;
    },
    getCompanyEmailEnabled(state) {
      return state.companyEmailEnabled;
    },
    getStorefrontPricing(state) {
      return state.storefrontPricing;
    },
    getDisableNextButton(state) {
      return state.disableNextButton;
    },
    getPredefinedCheckoutFields() {
      return [
        "First Name",
        "Last Name",
        "Company Email",
        "Phone",
        "Department",
        "Location",
        "Manager",
        "Shopper Location",
      ];
    },
    getDistributorLoadPending(state) {
      return state.distributorLoadPending;
    },
    getCountries() {
      return {
        "United States": "US",
      };
    },
    getStates() {
      return {
        Alabama: "AL",
        Alaska: "AK",
        Arizona: "AZ",
        Arkansas: "AR",
        California: "CA",
        Colorado: "CO",
        Connecticut: "CT",
        Delaware: "DE",
        "District of Columbia": "DC",
        Florida: "FL",
        Georgia: "GA",
        Hawaii: "HI",
        Idaho: "ID",
        Illinois: "IL",
        Indiana: "IN",
        Iowa: "IA",
        Kansas: "KS",
        Kentucky: "KY",
        Louisiana: "LA",
        Maine: "ME",
        Maryland: "MD",
        Massachusetts: "MA",
        Michigan: "MI",
        Minnesota: "MN",
        Mississippi: "MS",
        Missouri: "MO",
        Montana: "MT",
        Nebraska: "NE",
        Nevada: "NV",
        "New Hampshire": "NH",
        "New Jersey": "NJ",
        "New Mexico": "NM",
        "New York": "NY",
        "North Carolina": "NC",
        "North Dakota": "ND",
        Ohio: "OH",
        Oklahoma: "OK",
        Oregon: "OR",
        Pennsylvania: "PA",
        "Rhode Island": "RI",
        "South Carolina": "SC",
        "South Dakota": "SD",
        Tennessee: "TN",
        Texas: "TX",
        Utah: "UT",
        Vermont: "VT",
        Virginia: "VA",
        Washington: "WA",
        "West Virginia": "WV",
        Wisconsin: "WI",
        Wyoming: "WY",
      };
    },

    getProductTaxCodes() {
      return {
        Clothing: "STP-PCC-00246",
        "Non-Clothing Accessories": "STP-PCC-00662",
        "Safety Clothing": "STP-PCC-00071",
        "Luggage and Briefcase": "STP-PCC-00382",
        Sweatbands: "STP-PCC-00430",
        Bandanas: "STP-PCC-00432",
        "School Uniforms": "STP-PCC-00677",
        "Book Bags": "STP-PCC-01029",
        Other: "STP-PCC-00246",
      };
    },

    getDecorationPlacements(state) {
      return state.decorationPlacements;
    },

    getDecorationNames(state) {
      return state.decorationNames;
    },

    getIsProductsLoaded(state) {
      return state.productsLoaded;
    },
    getIsNewOrderAdded(state) {
      return state.newOrderAdded;
    },
    getBrandColor(state) {
      return state.brandColor;
    },
    getStoreName(state) {
      return state.storeName;
    },
    getStoreSlug(state) {
      return state.storeSlug;
    },
    getBrandLogoUrl(state) {
      return state.brandLogoUrl;
    },
    getBrandFaviconUrl(state) {
      return state.brandFaviconUrl;
    },
    getStorefrontWhiteLabel(state) {
      return state.storefrontWhiteLabel;
    },
    getShippingCarriers() {
      return ["FedEx", "UPS", "USPS"];
    },
    getShippingTypesFromCarriers() {
      return {
        FedEx: ["FedEx 2Day", "FedEx Ground", "FedEx Standard Overnight"],
        UPS: ["UPS 3 Day Select", "UPS Ground", "UPS Next Day Air"],
        USPS: ["USPS Priority Mail Express", "USPS Ground Parsel Select"],
      };
    },
    getInternationalShippingTypesFromCarriers() {
      return {
        FedEx: [
          "FedEx Ground International",
          "FedEx International Economy",
          "FedEx International Priority",
        ],
        UPS: [
          "UPS Standard Internaitonal",
          "UPS Worldwide Expedited",
          "UPS Worldwide Saver",
        ],
        USPS: [
          "USPS First Class Mail International",
          "USPS Priority Mail International",
        ],
      };
    },
    getProductsIncomplete(state) {
      return state.productsIncomplete;
    },
    getBFLCostChanged(state) {
      return state.isBFLCostChanged;
    },
    getIsClickedExitButton(state) {
      return state.isClickedExitButton;
    },
    getTabsIncomplete(state) {
      return state.tabsIncomplete;
    },
    getProductEditModalPopped(state) {
      return state.productEditModalPopped;
    },
    getFieldCustomEnabled(state) {
      return state.fieldCustomEnabled;
    },
    getShopperAccountChanged(state) {
      return state.shopperAccountChanged;
    },
    getShopperURLChanged(state) {
      return state.shopperURLChanged;
    },
    getLocationsChanged(state) {
      return state.locationsChanged;
    },
  },
};
